import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import styles from 'css/src/components/agent/AIMediaResult.module.scss';

import React, { useState } from 'react';
import { AspectRatio, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type ToolExecutionContext,
} from '../types';

import { Tool, type ToolConstructorArgs } from './Tool';
import type {
    AssetContainerModel,
    QueuedTaskResultWithGenerationLimits,
    SlideBackgroundModel,
    SupportedAIMediaAspectRatios,
} from 'js/src/client';
import {
    ApiService,
    ErrorEnum,
    GenerativeMediaErrorCodes,
    ImageGenerationRequestModelEnum,
    TaskResultMessageTypeEnum,
    UsageSourceEnum,
    VideoGenerationRequestModelEnum,
} from 'js/src/client';
import AnimationMinutesBadge from 'js/src/agent/components/AnimationMinutesBadge';
import Spinner from 'js/src/components/common/ui/Spinner';
import { saveSpecificVideoOperation } from 'js/src/data/app/app-operations';
import { getVideoId } from 'js/src/data/app/app-selectors';
import { convertSlideBackgroundToAssetContainer } from 'js/src/data/media/media-types';
import { getAIMediaModels } from 'js/src/data/media/media-selectors';
import { DEFAULT_MAX_PROMPT_LENGTH, getMaxPromptLength } from 'js/src/data/media/media-utils';
import { getUsageLimits } from 'js/src/data/usage-limit/usage-limit-selectors';
import {
    updateAnimationSecondsUsedInCycle,
    updateImageGenerationsUsedInCycle,
    updateTrialAnimationSecondsUsed,
} from 'js/src/data/usage-limit/usage-limit-slice';
import { formatAnimationMinutesCost } from 'js/src/components/animated-explainer-videos/utils/billingUtils';
import Flags from 'js/src/libs/flags';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    isApiError,
    isApiErrorWithProperty,
    isErrorObject,
    OperationAbortedError,
} from 'js/src/types';

// Free-plan users have no animation minutes, so the backend rejects AI video
// generation with a feature-limit error. We surface the same upgrade prompt both
// proactively (before calling the API, when we already know the plan has no
// allowance) and as a fallback when the API returns the limit error anyway.
const VIDEO_UPGRADE_REASON = `Generating AI video clips requires animation minutes. Use the ${TOOL_NAMES.suggestUpgrade} tool with feature "generate_ai_video_clip" to show the user that they need to upgrade.`;
const VIDEO_UPGRADE_MESSAGE = `Error: ${VIDEO_UPGRADE_REASON}`;
// Not reported as a failure: the step stays visible so the card can offer the top-up.
const MINUTES_USED_UP_MESSAGE = `No clip was generated: the user has used up their animation minutes. The chat card already shows their balance with a way to get more, so do not call ${TOOL_NAMES.suggestUpgrade}; tell the user in one short sentence.`;

// The generation runs inside the tool call. Images are usually seconds; Gemini Omni Flash video
// has been observed past ten minutes at the tail, the same ceiling GenX uses.
const IMAGE_TIMEOUT_MS = 5 * 60 * 1000;
const VIDEO_TIMEOUT_MS = 15 * 60 * 1000;
const GENERATION_TIMEOUT_REASON = 'ai-media-generation-timeout';

const VIDEO_DURATION_S = 5;

// Gemini Omni Flash only renders 16:9 and 9:16; square and 4:5 videos get the nearest and the
// result says so, so the agent can reframe the clip in the scene rather than retry.
const VIDEO_ASPECT_RATIOS: Record<AspectRatio, '16:9' | '9:16'> = {
    [AspectRatio.LANDSCAPE]: '16:9',
    [AspectRatio.VERTICAL]: '9:16',
    [AspectRatio.SQUARE]: '16:9',
    [AspectRatio.FOURFIVE]: '9:16',
};

export const generateAIMediaSchema = z.object({
    prompt: z
        .string()
        .describe(
            `What to generate, or, with reference_media_id, what to change in that image. Be descriptive and specific for best results. Standard models accept up to ${DEFAULT_MAX_PROMPT_LENGTH} characters and premium models accept longer prompts; a prompt over the model's limit is rejected and the error states the limit. If calling multiple times use the same descriptors to get media that match in style.`,
        ),
    reference_media_id: z
        .string()
        .optional()
        .describe(
            'Optional: the id of an image to edit or build on — one in a scene (its mediaId in the video state), one this tool generated earlier (the id in its result), or a search result. For images, the prompt is applied to that image, so to change an existing image pass its id here and write the prompt as the change ("turn the tea into a cappuccino"). For videos, the image is animated into the clip. The reference must be an image, not a video.',
        ),
    aspect_ratio: z
        .enum(VideoData.ALL_ASPECT_RATIOS)
        .optional()
        .describe(
            'Optional: Aspect ratio for generated media. Defaults to landscape. Options: landscape (16:9), square (1:1), vertical (9:16), fourfive (4:5). Video only renders landscape or vertical; square is generated as landscape and fourfive as vertical, and the result says so.',
        ),
    media_type: z
        .enum(['image', 'video'])
        .optional()
        .describe(
            `Type of media to generate. "image" generates one AI image. "video" generates one ${VIDEO_DURATION_S}-second AI video clip. Defaults to "image".`,
        ),
});
export type AIMediaArgs = z.infer<typeof generateAIMediaSchema>;

export type AiMediaQuality = 'standard' | 'premium';

export type PremiumUnavailableReason = 'no_plan' | 'quota_exhausted';

export interface AIMediaResult {
    media: { id: string; type: string; title: string; tags: string }[];
    quality: AiMediaQuality;
    /** The generation model that produced the media. */
    model: string;
    /** Anything the agent should know, such as a video rendered at a different aspect ratio. */
    note?: string;
    /** Video only: the animation minutes left after this clip was charged, e.g. "4 mins 55s". */
    animationMinutesRemaining?: string;
}

export function pickImageModel(
    quality: AiMediaQuality,
    hasReference: boolean,
): ImageGenerationRequestModelEnum {
    if (quality === 'premium') {
        return hasReference
            ? ImageGenerationRequestModelEnum.FAL_AI_NANO_BANANA_2_EDIT
            : ImageGenerationRequestModelEnum.FAL_AI_NANO_BANANA_2;
    }
    return hasReference
        ? ImageGenerationRequestModelEnum.FAL_AI_FLUX_KONTEXT_DEV
        : ImageGenerationRequestModelEnum.FAL_AI_FLUX_SCHNELL;
}

export function pickVideoModel(hasReference: boolean): VideoGenerationRequestModelEnum {
    return hasReference
        ? VideoGenerationRequestModelEnum.GOOGLE_GEMINI_OMNI_FLASH_REFERENCE_TO_VIDEO
        : VideoGenerationRequestModelEnum.GOOGLE_GEMINI_OMNI_FLASH;
}

const isFeatureLimitError = (error: unknown): boolean =>
    isApiErrorWithProperty(error, 'error_code') &&
    error.body.error_code === ErrorEnum.HIT_FEATURE_USAGE_LIMIT;

const isModelNotAllowedError = (error: unknown): boolean =>
    isApiError(error) && error.status === 403 && !isFeatureLimitError(error);

/**
 * Generates one AI image or one short AI video clip and returns it as registry media. Images
 * prefer the premium model and fall back to the standard one when the plan or quota rules it
 * out; video generations need animation minutes. The generation runs inside the
 * call, so the agent gets the media id back and decides where it goes.
 */
export class GenerateAIMediaTool extends Tool<typeof generateAIMediaSchema> {
    protected override readonly schema = generateAIMediaSchema;
    protected override readonly toolName = TOOL_NAMES.generateAiMedia;
    public override readonly subagentAccess = SubagentAccess.READ_ONLY;
    protected override readonly toolDescription = `Generate AI media (one image, or one ${VIDEO_DURATION_S}-second video clip) from a text prompt, or edit an existing image by passing it as reference_media_id with the change as the prompt. Returns the media's id once it is ready; it is not placed anywhere.
- When the user said where it goes, place it with ${TOOL_NAMES.setMedia} and stop: do not run ${TOOL_NAMES.reviewSceneImages} or ${TOOL_NAMES.lintVideo} afterwards. When they did not, describe it and ask whether to use it and where. When a recipe is building the video, follow the recipe.
- Generate exactly what was asked, once. Never generate extra media to fill other slots, replace media nobody mentioned, or satisfy a reviewer. Fix framing with ${TOOL_NAMES.editMedia} rather than regenerating; generate again only when the user asks or the result failed.
- Images use the premium model when the plan allows and say so when they fall back to the standard one; video needs animation minutes.`;

    public override canRunAsync = true;
    public override customRendererMode = CustomRendererMode.INLINE;
    public override customRenderer: React.FC<CustomRendererComponentProps>;

    constructor(args: ToolConstructorArgs) {
        super(args);
        this.customRenderer = (props) => (
            <AIMediaResultRenderer
                {...props}
                onBeforeRedirectToCheckout={this.saveVideoBeforeCheckout}
            />
        );
    }

    // Buying minutes leaves for Stripe checkout; save first so the agent's edits aren't lost.
    private saveVideoBeforeCheckout = async (): Promise<void> => {
        const videoId = getVideoId(this.getState());
        if (videoId === null) {
            return;
        }
        const { saveSpecificVideo } = makeOperationsCallable(
            { saveSpecificVideoOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        await saveSpecificVideo({ videoId, videoData: null, isForceSave: false });
    };

    public override getToolDisplayName(): string {
        return 'Generating AI media';
    }

    public get statusIcon(): string {
        return 'auto_awesome';
    }

    /**
     * Mirrors the backend gate on AI video generation. The backend charges the plan's pool when it
     * has one and trial minutes only otherwise, refusing a clip the balance cannot cover. When
     * usage limits haven't loaded we defer to the API rather than block a paid user on stale state.
     */
    private videoMinutesStatus(): 'available' | 'no_minutes' | 'used_up' {
        const usageLimits = getUsageLimits(this.getState());
        if (!usageLimits?.isLoaded) {
            return 'available';
        }
        const poolLimit = usageLimits.animation_seconds_limit_per_cycle ?? 0;
        const trialLimit = usageLimits.trial_animation_seconds_limit ?? 0;
        const remaining =
            poolLimit > 0
                ? poolLimit - (usageLimits.animation_seconds_used_in_cycle ?? 0)
                : trialLimit - (usageLimits.trial_animation_seconds_used ?? 0);
        if (poolLimit <= 0 && trialLimit <= 0) {
            return 'no_minutes';
        }
        return remaining < VIDEO_DURATION_S ? 'used_up' : 'available';
    }

    /** The model the generation will run on, so the prompt cap checked up front is that model's. */
    private modelForRequest(
        mediaType: AIMediaArgs['media_type'],
        hasReference: boolean,
    ): ImageGenerationRequestModelEnum | VideoGenerationRequestModelEnum {
        if (mediaType === 'video') {
            return pickVideoModel(hasReference);
        }
        const quality: AiMediaQuality = this.premiumImageUnavailable() ? 'standard' : 'premium';
        return pickImageModel(quality, hasReference);
    }

    private maxPromptLengthFor(
        modelName: ImageGenerationRequestModelEnum | VideoGenerationRequestModelEnum,
    ): number {
        const modelConfig = getAIMediaModels(this.getState()).find(
            (model) => (model.model_name as string) === modelName,
        );
        return getMaxPromptLength(modelConfig);
    }

    /**
     * Premium image models need a paid plan and count against the monthly premium image quota.
     * A free plan reports no quota at all. When limits haven't loaded we try premium and let the
     * API's answer decide.
     */
    private premiumImageUnavailable(): PremiumUnavailableReason | null {
        const usageLimits = getUsageLimits(this.getState());
        if (!usageLimits?.isLoaded) {
            return null;
        }
        const limit = usageLimits.image_generations_limit_per_cycle;
        if (limit === null || limit === undefined || limit <= 0) {
            return 'no_plan';
        }
        return (usageLimits.image_generations_used_in_cycle ?? 0) < limit
            ? null
            : 'quota_exhausted';
    }

    public async execute(args: AIMediaArgs, context: ToolExecutionContext): Promise<string> {
        const { prompt, reference_media_id, aspect_ratio, media_type = 'image' } = args;

        const maxPromptLength = this.maxPromptLengthFor(
            this.modelForRequest(media_type, reference_media_id !== undefined),
        );
        if (prompt.length > maxPromptLength) {
            return `Error: Prompt is too long (${prompt.length} characters). Maximum length is ${maxPromptLength} characters. Please shorten your description and focus on key visual details.`;
        }
        if (!prompt.trim()) {
            return 'Error: Prompt cannot be empty. Please provide a description of the media you want to generate.';
        }

        const mediaRegistry = MediaRegistry.getInstance();
        let assetUuid: string | undefined;
        if (reference_media_id) {
            if (!mediaRegistry.hasItem(reference_media_id)) {
                return `Error: Unknown media ${reference_media_id}`;
            }
            const mediaData = mediaRegistry.getItem(reference_media_id);
            if (!mediaData) {
                return `Error: Could not retrieve media data for ${reference_media_id}`;
            }
            const imageUuid = mediaData.background?.image?.uuid;
            const videoUuid = mediaData.background?.video?.uuid;
            if (videoUuid && !imageUuid) {
                return `Error: Reference media ${reference_media_id} is a video. Only images can be used as reference for media generation.`;
            }
            if (!imageUuid) {
                return `Error: Could not extract asset UUID from media ${reference_media_id}`;
            }
            assetUuid = imageUuid;
        }

        try {
            if (media_type === 'video') {
                const minutesStatus = this.videoMinutesStatus();
                if (minutesStatus === 'no_minutes') {
                    return VIDEO_UPGRADE_MESSAGE;
                }
                if (minutesStatus === 'used_up') {
                    return MINUTES_USED_UP_MESSAGE;
                }
                const requestedRatio = aspect_ratio ?? AspectRatio.LANDSCAPE;
                const videoRatio = VIDEO_ASPECT_RATIOS[requestedRatio];
                const videoModel = pickVideoModel(assetUuid !== undefined);
                const { taskId, animationMinutesRemaining } = await this.startVideo(
                    videoModel,
                    prompt,
                    assetUuid,
                    videoRatio,
                );
                const result = await this.waitForResult(taskId, 'video_generation', context);
                const ratioNote =
                    VideoData.getAspectRatioDisplayRatio(requestedRatio) === videoRatio
                        ? undefined
                        : `AI video only renders landscape (16:9) or vertical (9:16), so this ${requestedRatio} clip was generated at ${videoRatio}; reframe it in the scene with ${TOOL_NAMES.editMedia} if needed.`;
                return this.registerResult(result, 'premium', videoModel, mediaRegistry, {
                    note: ratioNote,
                    animationMinutesRemaining,
                });
            }

            let premiumUnavailable = this.premiumImageUnavailable();
            let quality: AiMediaQuality = premiumUnavailable ? 'standard' : 'premium';
            let taskId: string;
            try {
                taskId = await this.startImage(quality, prompt, assetUuid, aspect_ratio);
            } catch (error) {
                // The plan or quota check was stale (or limits hadn't loaded): fall back to the
                // standard model rather than fail the generation.
                if (quality !== 'premium') {
                    throw error;
                }
                if (isFeatureLimitError(error)) {
                    premiumUnavailable = 'quota_exhausted';
                } else if (isModelNotAllowedError(error)) {
                    premiumUnavailable = 'no_plan';
                } else {
                    throw error;
                }
                quality = 'standard';
                taskId = await this.startImage(quality, prompt, assetUuid, aspect_ratio);
            }
            const result = await this.waitForResult(taskId, 'image_generation', context);
            return this.registerResult(
                result,
                quality,
                pickImageModel(quality, assetUuid !== undefined),
                mediaRegistry,
            );
        } catch (error) {
            // Limits had loaded and showed minutes, so a refusal on minutes means they ran out since.
            if (
                media_type === 'video' &&
                isFeatureLimitError(error) &&
                getUsageLimits(this.getState())?.isLoaded
            ) {
                return MINUTES_USED_UP_MESSAGE;
            }
            return `Error generating media: ${describeGenerationError(error, media_type)}`;
        }
    }

    private async startImage(
        quality: AiMediaQuality,
        prompt: string,
        assetUuid: string | undefined,
        aspectRatio: AspectRatio | undefined,
    ): Promise<string> {
        const response = await ApiService.imageGenerationCreate({
            body: {
                prompt: prompt.trim(),
                model: pickImageModel(quality, assetUuid !== undefined),
                aspect_ratio: aspectRatio
                    ? (VideoData.getAspectRatioDisplayRatio(
                          aspectRatio,
                      ) as SupportedAIMediaAspectRatios)
                    : null,
                asset_uuids: assetUuid ? [assetUuid] : [],
                usage_source: UsageSourceEnum.TOOLBOX,
            },
        });
        // Keep the quota counters current, as the AI Media tab does: the tab's remaining count
        // and this tool's premium check both read them.
        this.dispatch(
            updateImageGenerationsUsedInCycle(response.data.image_generations_used_in_cycle),
        );
        return response.data.task_id;
    }

    private async startVideo(
        model: VideoGenerationRequestModelEnum,
        prompt: string,
        assetUuid: string | undefined,
        aspectRatio: '16:9' | '9:16',
    ): Promise<{ taskId: string; animationMinutesRemaining?: string }> {
        const response = await ApiService.videoGenerationCreate({
            body: {
                prompt: prompt.trim(),
                model,
                aspect_ratio: aspectRatio,
                duration: VIDEO_DURATION_S,
                asset_uuids: assetUuid ? [assetUuid] : [],
                usage_source: UsageSourceEnum.TOOLBOX,
            },
        });
        const limits = response.data;
        if (limits.animation_seconds_used_in_cycle > 0) {
            this.dispatch(
                updateAnimationSecondsUsedInCycle(limits.animation_seconds_used_in_cycle),
            );
        }
        if (limits.trial_animation_seconds_used > 0) {
            this.dispatch(updateTrialAnimationSecondsUsed(limits.trial_animation_seconds_used));
        }
        return {
            taskId: limits.task_id,
            animationMinutesRemaining: describeRemainingAnimationMinutes(limits),
        };
    }

    /**
     * Waits for the task result over the websocket, the same path the AI Media tab uses. Stop in
     * the chat aborts the wait through the turn's abort signal.
     */
    private async waitForResult(
        taskId: string,
        taskName: 'image_generation' | 'video_generation',
        context: ToolExecutionContext,
    ): Promise<SlideBackgroundModel> {
        const abortController = new AbortController();
        const onTurnAbort = () => abortController.abort(context.abortSignal?.reason);
        // The turn may already have been stopped while the request was being accepted.
        if (context.abortSignal?.aborted) {
            onTurnAbort();
        }
        context.abortSignal?.addEventListener('abort', onTurnAbort);
        const timeoutMs = taskName === 'video_generation' ? VIDEO_TIMEOUT_MS : IMAGE_TIMEOUT_MS;
        const timeoutId = window.setTimeout(
            () => abortController.abort(GENERATION_TIMEOUT_REASON),
            timeoutMs,
        );
        try {
            if (abortController.signal.aborted) {
                throw Object.assign(new Error('Media generation was cancelled'), {
                    name: 'AbortError',
                });
            }
            return await WebSocketClient.getInstance().waitForMessage<SlideBackgroundModel>(
                taskId,
                TaskResultMessageTypeEnum.TASK_RESULT,
                taskName,
                { abortController, timeoutMs },
            );
        } catch (error) {
            if (abortController.signal.reason === GENERATION_TIMEOUT_REASON) {
                throw new Error(
                    `Media generation timed out after ${Math.round(timeoutMs / 60000)} minutes. Please try again.`,
                );
            }
            throw error;
        } finally {
            window.clearTimeout(timeoutId);
            context.abortSignal?.removeEventListener('abort', onTurnAbort);
        }
    }

    private registerResult(
        result: SlideBackgroundModel,
        quality: AiMediaQuality,
        model: string,
        mediaRegistry: MediaRegistry,
        extras: Pick<AIMediaResult, 'note' | 'animationMinutesRemaining'> = {},
    ): string {
        const id = mediaRegistry.addItem(convertSlideBackgroundToAssetContainer(result));
        const representation = mediaRegistry.getRepresentation(id);
        const payload: AIMediaResult = {
            media: representation ? [representation] : [],
            quality,
            model,
            ...(extras.note ? { note: extras.note } : {}),
            ...(extras.animationMinutesRemaining
                ? { animationMinutesRemaining: extras.animationMinutesRemaining }
                : {}),
        };
        return JSON.stringify(payload, null, 2);
    }
}

// The backend charges the plan pool first and trial minutes only when there is no pool, and
// reports the balance it charged against; the other one comes back as zeros.
function describeRemainingAnimationMinutes(
    limits: QueuedTaskResultWithGenerationLimits,
): string | undefined {
    if (
        limits.animation_seconds_limit_per_cycle !== null &&
        limits.animation_seconds_limit_per_cycle > 0
    ) {
        return formatAnimationMinutesCost(
            limits.animation_seconds_limit_per_cycle - limits.animation_seconds_used_in_cycle,
        );
    }
    if (limits.trial_animation_seconds_limit > 0) {
        return formatAnimationMinutesCost(
            limits.trial_animation_seconds_limit - limits.trial_animation_seconds_used,
        );
    }
    return undefined;
}

function describeGenerationError(error: unknown, mediaType: 'image' | 'video'): string {
    if (
        (isErrorObject(error) && error.error instanceof OperationAbortedError) ||
        (error instanceof Error && error.name === 'AbortError')
    ) {
        return 'Media generation was cancelled';
    }
    if (mediaType === 'video' && isFeatureLimitError(error)) {
        return VIDEO_UPGRADE_REASON;
    }
    if (isErrorObject(error) && error.result !== undefined) {
        switch (error.result?.type) {
            case GenerativeMediaErrorCodes.NSFW:
                return 'This request goes against our AI terms of service. Please try a different prompt.';
            case GenerativeMediaErrorCodes.USER_CANCELLED:
                return 'Media generation was cancelled';
            case GenerativeMediaErrorCodes.NO_MEDIA_GENERATED:
                return 'Nothing was generated. Try a different reference image or a more descriptive prompt.';
            // No default
        }
    }
    if (isApiErrorWithProperty(error, 'prompt') && Array.isArray(error.body.prompt)) {
        const promptError = error.body.prompt[0];
        if (typeof promptError === 'string' && promptError) {
            return promptError;
        }
    }
    if (isErrorObject(error) && error.error.message) {
        return error.error.message;
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'An error occurred while generating media';
}

const getImageUrlFromAsset = (assetData: AssetContainerModel): string | null =>
    assetData.background?.image?.preview_url ||
    assetData.background?.image?.thumbnail_url ||
    assetData.background?.video?.poster_url ||
    null;

const getVideoUrlFromAsset = (assetData: AssetContainerModel): string | null =>
    assetData.background?.video?.preview_url || assetData.background?.video?.thumbnail_url || null;

function parseResult(result: string): AIMediaResult | null {
    try {
        const parsed = JSON.parse(result) as Partial<AIMediaResult>;
        return Array.isArray(parsed.media) ? (parsed as AIMediaResult) : null;
    } catch {
        return null;
    }
}

/**
 * The tool's card in the chat: a loading placeholder while the generation runs (the tool call is
 * pending), then the media; a failed call renders nothing.
 */
// Staff only, and collapsed by default: the full prompt is written for the model.
const AIMediaPromptToggle: React.FC<{ prompt: string }> = ({ prompt }) => {
    const [showPrompt, setShowPrompt] = useState(false);
    if (!Flags.canUseStaffControls()) {
        return null;
    }
    return (
        <div className="d-flex flex-column mb-l5-8">
            <button
                type="button"
                className={`${styles['ai-media-prompt-toggle']} btn btn-link p-l5-0 text-decoration-none align-self-start d-inline-flex align-items-center gap-2`}
                onClick={() => setShowPrompt((prev) => !prev)}
                aria-expanded={showPrompt}
            >
                <span className="font-l5 font-l5--subtext">Show prompt</span>
                <i
                    translate="no"
                    className={`material-symbols-outlined ${styles['ai-media-prompt-chevron']} ${
                        showPrompt ? styles['ai-media-prompt-chevron--expanded'] : ''
                    }`}
                    aria-hidden="true"
                >
                    chevron_right
                </i>
            </button>
            {showPrompt && (
                <div className="mt-l5-4 font-l5 font-l5--subtext color-grey-600">
                    &quot;{prompt}&quot;
                </div>
            )}
        </div>
    );
};

const AIMediaResultRenderer: React.FC<
    CustomRendererComponentProps & { onBeforeRedirectToCheckout?: () => Promise<void> }
> = ({ isLoading, result, toolCall, onBeforeRedirectToCheckout }) => {
    const args = toolCall ? (JSON.parse(toolCall.function.arguments) as Partial<AIMediaArgs>) : {};
    const prompt = args.prompt || 'Unknown prompt';
    const mediaType = args.media_type === 'video' ? 'video' : 'image';

    if (isLoading) {
        return (
            <div className={styles['ai-media-result-container']}>
                <AIMediaPromptToggle prompt={prompt} />
                <div
                    className={`border rounded d-flex flex-column align-items-center justify-content-center ${styles['ai-media-pending']}`}
                    data-testid="ai-media-pending-card"
                >
                    <Spinner />
                    <span className="font-l5 font-l5--subtext color-grey-600 mt-l5-8">
                        {mediaType === 'video'
                            ? 'Video clips can take a few minutes'
                            : 'This usually takes under a minute'}
                    </span>
                </div>
                {mediaType === 'video' && (
                    <AnimationMinutesBadge
                        onBeforeRedirectToCheckout={onBeforeRedirectToCheckout}
                    />
                )}
            </div>
        );
    }

    if (result === MINUTES_USED_UP_MESSAGE) {
        return (
            <AnimationMinutesBadge isOut onBeforeRedirectToCheckout={onBeforeRedirectToCheckout} />
        );
    }

    // A failed call's message is for the agent; the step keeps it behind its own expand.
    const parsed = parseResult(result);
    if (!parsed) {
        return null;
    }

    const mediaRegistry = MediaRegistry.getInstance();
    return (
        <div className={styles['ai-media-result-container']}>
            <AIMediaPromptToggle prompt={prompt} />
            {parsed.media.map((item) => {
                const data = mediaRegistry.getItem(item.id);
                if (!data) {
                    return null;
                }
                if (data.background?.video) {
                    const videoUrl = getVideoUrlFromAsset(data);
                    return videoUrl ? (
                        <video
                            key={item.id}
                            src={videoUrl}
                            controls
                            className={styles['ai-media-video']}
                        />
                    ) : null;
                }
                const imageUrl = getImageUrlFromAsset(data);
                return imageUrl ? (
                    <div
                        key={item.id}
                        className={`border rounded ${styles['ai-media-image-wrapper']}`}
                    >
                        <img src={imageUrl} alt={item.title} className={styles['ai-media-image']} />
                    </div>
                ) : null;
            })}
            {parsed.note && (
                <div className="mt-l5-4 font-l5 font-l5--subtext color-grey-600">{parsed.note}</div>
            )}
            {mediaType === 'video' && (
                <AnimationMinutesBadge onBeforeRedirectToCheckout={onBeforeRedirectToCheckout} />
            )}
            {Flags.canUseStaffControls() && (
                <div
                    className="mt-l5-4 font-l5 font-l5--subtext color-grey-500"
                    data-testid="ai-media-model-note"
                >
                    Model: {parsed.model}
                </div>
            )}
        </div>
    );
};
